import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Building2, User, CheckCircle, ThumbsUp, ThumbsDown } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { StarRating } from "@/components/reviews/StarRating";

interface ReviewDetailModalProps {
  review: any;
  isOpen: boolean;
  onClose: () => void;
  onRespond: (reviewId: string, responseText: string) => Promise<void>;
}

export function ReviewDetailModal({
  review,
  isOpen,
  onClose,
  onRespond,
}: ReviewDetailModalProps) {
  const { t } = useTranslation();
  const [responseText, setResponseText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!review) return null;

  const handleSubmitResponse = async () => {
    if (!responseText.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onRespond(review.id, responseText);
      setResponseText("");
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setResponseText("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl bg-card border-[3px] border-dark shadow-[8px_8px_0px_#101322]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Type Badge */}
              {review.review_type === "company" ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold border-[2px] border-blue-700">
                  <Building2 className="w-3 h-3" />
                  {t("reviews.companyReview", "Azienda")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold border-[2px] border-amber-700">
                  <User className="w-3 h-3" />
                  {t("reviews.profileReview", "Profilo")}
                </span>
              )}
              <StarRating rating={review.rating} readonly size="sm" />
              <span className="text-sm text-muted-foreground">
                {format(new Date(review.created_at), "dd MMM yyyy")}
              </span>
            </div>
          </div>
          <DialogTitle className="font-heading text-2xl font-bold text-dark mt-4">
            {review.title}
          </DialogTitle>
        </DialogHeader>

        {/* Review Content - Scrollable */}
        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Comment */}
            <div>
              <p className="text-foreground leading-relaxed">{review.comment}</p>
            </div>

            {/* Pros - se esistono */}
            {review.pros && (
              <div className="bg-success/10 border-l-[4px] border-success p-4">
                <h4 className="font-bold text-dark mb-2 flex items-center gap-2">
                  <ThumbsUp className="w-4 h-4 text-success" />
                  {t("reviews.pros", "Punti di forza")}
                </h4>
                <p className="text-foreground leading-relaxed">{review.pros}</p>
              </div>
            )}

            {/* Cons - se esistono */}
            {review.cons && (
              <div className="bg-destructive/10 border-l-[4px] border-destructive p-4">
                <h4 className="font-bold text-dark mb-2 flex items-center gap-2">
                  <ThumbsDown className="w-4 h-4 text-destructive" />
                  {t("reviews.cons", "Punti di debolezza")}
                </h4>
                <p className="text-foreground leading-relaxed">{review.cons}</p>
              </div>
            )}

            {/* Existing Response */}
            {review.response_text && (
              <div className="bg-surface border-l-[4px] border-primary p-4">
                <div className="flex items-center gap-2 font-bold mb-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  {t("reviews.yourResponse")}
                </div>
                <p className="text-sm text-foreground">{review.response_text}</p>
                {review.response_date && (
                  <div className="text-xs text-muted-foreground mt-2">
                    {t("reviews.respondedOn", "Risposto il")}{" "}
                    {format(new Date(review.response_date), "dd MMM yyyy")}
                  </div>
                )}
              </div>
            )}

            {/* Response Form - Only if not already responded */}
            {!review.response_text && (
              <div className="space-y-4">
                <label className="block font-bold text-dark">
                  {t("reviews.writeResponse", "Scrivi la tua risposta")}
                </label>
                <textarea
                  className="w-full min-h-32 px-4 py-3 border-[3px] border-dark bg-white focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  placeholder={t("reviews.responsePlaceholder", "Scrivi una risposta professionale...")}
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                />
                <div className="flex gap-3 justify-end">
                  <BrutalButton
                    variant="outline"
                    onClick={handleClose}
                  >
                    {t("common.cancel", "Annulla")}
                  </BrutalButton>
                  <BrutalButton
                    onClick={handleSubmitResponse}
                    disabled={!responseText.trim() || isSubmitting}
                  >
                    {isSubmitting
                      ? t("common.sending", "Invio...")
                      : t("reviews.sendResponse", "Invia risposta")}
                  </BrutalButton>
                </div>
              </div>
            )}

            {/* Close button if already responded */}
            {review.response_text && (
              <div className="flex justify-end pt-4">
                <BrutalButton variant="outline" onClick={handleClose}>
                  {t("common.close", "Chiudi")}
                </BrutalButton>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
